import { ChangeEvent, FC, useState } from 'react';
import { Divider, Flex, Stack } from '@chakra-ui/react';
import { isSIREN, isSIRET } from '@shinetools/vat-siren-siret';
import { isEmpty, omit } from 'ramda';

import zodLocales from 'common/zod/locales';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineModal from 'components/_core/SunshineModal';
import CountryPicker from 'components/CountryPicker';
import Initial from 'components/Initial';
import { useModal } from 'components/ModalProvider/hooks';
import { StepProps } from 'components/Stepper/types';
import { logEvent } from 'features/Analytics/analytics';

import { useClientModalContext } from '../../../Modals';
import { ClientModalType } from '../../../Modals/types';
import ClientFormWrapper from '../../components/ClientFormWrapper';
import {
  ClientForm,
  createEmptyClientContact,
  useClientFormModalContext,
} from '../../hooks/useClientFormModalContext';
import { useOrganizationFormFlowContext } from '../hooks/useOrganizationFormFlowContext';
import ExernalCompaniesPicker from './ExernalCompaniesPicker';
import { ExternalCompany } from './ExernalCompaniesPicker/hooks/useCompanyLookupQuery';
import locales from './locales';

function isValidLegalRegistrationNumber(params: {
  country: string;
  legalRegistrationNumber: string;
}): boolean {
  const { country, legalRegistrationNumber } = params;

  // Only validate the legalRegistrationNumber of French companies
  if (country !== 'FR') {
    return true;
  }

  return (
    !isEmpty(legalRegistrationNumber) &&
    (isSIRET(legalRegistrationNumber) || isSIREN(legalRegistrationNumber))
  );
}

const isValidClient = (client: ClientForm) => {
  const { companyName, country, legalRegistrationNumber } = client;

  const requiredValues: Array<keyof ClientForm> = [
    'city',
    'companyName',
    'country',
    'street',
    'zip',
  ];

  return (
    !!companyName?.trim() &&
    requiredValues.every((key) => client[key]) &&
    (legalRegistrationNumber
      ? isValidLegalRegistrationNumber({ country, legalRegistrationNumber })
      : true)
  );
};

const CreateClientForm: FC<StepProps> = ({ goNext }) => {
  const { client, setClient } = useClientFormModalContext();
  const { setSelectedContact } = useOrganizationFormFlowContext();

  const canSaveClient = isValidClient(client);
  const { showModal } = useModal();
  const { setContact } = useClientModalContext();

  const [searchInput, setSearchInput] = useState<string>('');
  const [isSearchingCompanies, setIsSearchingCompanies] =
    useState<boolean>(false);

  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    setClient({ ...client, [e.target.name]: e.target.value });

  const mapExternalCompanyWithClient = (externalCompany: ExternalCompany) =>
    setClient({
      ...client,
      ...omit(['legalName', 'legalVATNumber'], externalCompany),
      companyName: externalCompany.legalName || '',
      vatId: externalCompany?.legalVATNumber || '',
    });

  const onSelectCountry = (value: string) =>
    onChange({
      target: { name: 'country', value },
    } as unknown as ChangeEvent<HTMLInputElement>);

  const onLegalRegistrationNumberChange = (value: string) => {
    setClient({ ...client, legalRegistrationNumber: value });
  };

  const isNewClient = !client.clientId;

  const {
    city,
    clientId,
    companyName = '',
    contacts,
    country,
    legalRegistrationNumber,
    street,
    vatId,
    zip,
  } = client;

  return (
    <>
      <SunshineModal.Header>
        {isSearchingCompanies ? (
          <SunshineModal.Header.IconButton
            icon="arrow-left"
            onClick={() => setIsSearchingCompanies(false)}
          />
        ) : null}
      </SunshineModal.Header>

      {isSearchingCompanies ? (
        <ExernalCompaniesPicker
          mapExternalCompanyWithClient={mapExternalCompanyWithClient}
          searchInput={searchInput}
          setIsSearchingCompanies={setIsSearchingCompanies}
          setSearchInput={setSearchInput}
        />
      ) : (
        <ClientFormWrapper
          canSubmit={canSaveClient}
          clientInput={client}
          showAlert={() => goNext({ key: 'client-save-changes-alert' })}
        >
          <Stack spacing="space-24">
            <Form.Field
              hint={locales.findClientDescription}
              label={locales.searchInputLabel}
            >
              <Form.Input
                defaultValue={searchInput}
                name="search"
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={() => setIsSearchingCompanies(true)}
                placeholder={locales.searchInputPlaceholder}
                rightIcon="search"
              />
            </Form.Field>

            <Divider />

            <Form.Field label={locales.companyName}>
              <Form.Input
                defaultValue={companyName}
                name="companyName"
                onChange={onChange}
                required
              />
            </Form.Field>

            <Form.Field label={locales.street}>
              <Form.Input
                defaultValue={street}
                name="street"
                onChange={onChange}
                required
              />
            </Form.Field>

            <Form.Field label={locales.city}>
              <Form.Input
                name="city"
                onChange={onChange}
                required
                value={city}
              />
            </Form.Field>

            <Form.Field label={locales.zip}>
              <Form.Input name="zip" onChange={onChange} required value={zip} />
            </Form.Field>

            <CountryPicker
              label={locales.country}
              onSelect={onSelectCountry}
              selected={country}
            />

            <Form.Field
              error={
                country === 'FR' &&
                legalRegistrationNumber !== '' &&
                !isSIREN(legalRegistrationNumber) &&
                !isSIRET(legalRegistrationNumber)
                  ? zodLocales.errors.invalidSirenOrSiret
                  : undefined
              }
              label={locales.legalRegistrationNumber}
            >
              <Form.Input
                name="legalRegistrationNumber"
                onChange={(e) =>
                  onLegalRegistrationNumberChange(e.target.value)
                }
                required={country === 'FR'}
                type={country === 'FR' ? 'siren-siret' : 'text'}
                value={legalRegistrationNumber}
              />
            </Form.Field>

            <Form.Field label={locales.vatId}>
              <Form.Input
                defaultValue={vatId || ''}
                name="vatId"
                onChange={onChange}
                type="text"
              />
            </Form.Field>

            {contacts.length > 0 ? (
              <Form.Field label={locales.contacts}>
                <SunshineCard.Group>
                  {contacts.map((contact) => {
                    const fullName = `${contact.firstName} ${contact.lastName}`;

                    return (
                      <SunshineCard
                        alignItems="center"
                        key={contact.email}
                        onClick={() => {
                          setSelectedContact(contact);
                          goNext({ key: 'client-contact-form' });
                        }}
                      >
                        <SunshineCard.Slot name="aside">
                          <Flex alignItems="center" justifyContent="center">
                            <Initial name={fullName} />
                          </Flex>
                        </SunshineCard.Slot>

                        <SunshineCard.Content>
                          <SunshineCard.Title>{fullName}</SunshineCard.Title>
                        </SunshineCard.Content>

                        {!isNewClient ? (
                          <SunshineCard.Slot name="action">
                            <IconButton
                              aria-label={locales.deleteContact}
                              icon="trash"
                              onClick={(e) => {
                                e.stopPropagation();
                                setContact({
                                  ...contact,
                                  clientId: contact.clientId ?? '',
                                });
                                showModal(ClientModalType.DeleteContact);
                              }}
                              size="md"
                              variant="secondary"
                            />
                          </SunshineCard.Slot>
                        ) : null}
                      </SunshineCard>
                    );
                  })}
                </SunshineCard.Group>
              </Form.Field>
            ) : null}

            <Button
              alignSelf="flex-start"
              icon="plus"
              iconPosition="left"
              marginTop="space-16"
              onClick={() => {
                logEvent({
                  name: 'Add New Client Contact',
                  properties: {
                    from: 'ClientFormModal',
                    invoiceClientId: clientId,
                  },
                });
                setSelectedContact(createEmptyClientContact());
                goNext({ key: 'client-contact-form' });
              }}
              variant="secondary"
            >
              {locales.addContact}
            </Button>
          </Stack>
        </ClientFormWrapper>
      )}
    </>
  );
};

export default CreateClientForm;
